import { NotificationSourceType } from '@/api/notifications/queries';
import { computed } from '@vue/composition-api';
import { useNotificationMdParser } from '@/_core/utils/markdown';

export class NotificationsState {
    storymapUrlTemplate: string = '';

    allNotifications: UserNotificationModel[] = [];

    storymapsWhereCommentingEnabled: string[] = [];

    visibleNotifications = computed(() => {
        if (this.allNotifications.length === 0) {
            return [];
        }

        return this.allNotifications.filter((x) => this.isDismissedNotificationsVisible || !x.isDismissed);
    });

    directMessages = computed(() => {
        if (this.allNotifications.length === 0) {
            return [];
        }

        const visible = this.allNotifications.filter((x) => this.isDismissedNotificationsVisible || !x.isDismissed);
        const directVisible = visible.filter((x) => x.isDirectMessage.value);

        return directVisible;
    });

    newNotifications = computed(() => {
        if (this.allNotifications.length === 0) {
            return [];
        }

        return this.allNotifications.filter((x) => !x.isDismissed);
    });

    isDismissedNotificationsVisible: boolean = false;

    isInitialized: boolean = false;
}

const directMessageTypes = [
    NotificationSourceType.MentionedInRoadmapCardComment,
    NotificationSourceType.MentionedInIdeaComment,
    NotificationSourceType.MentionedInRoadmapIdeaComment,
    NotificationSourceType.MentionedInBoardComment,
    NotificationSourceType.MentionedInStorymapCardDescription,
    NotificationSourceType.MentionedInCardComment,
    NotificationSourceType.MentionedInFeedbackComment,
    NotificationSourceType.FirstEnterToStorymap,
    NotificationSourceType.InvitedToStorymap,
    NotificationSourceType.InvitedToWorkspace,
    NotificationSourceType.InvitedToStorymapForWsMembers,
    NotificationSourceType.StorymapRoleChanged,
    NotificationSourceType.WorkspaceRoleChanged,
];

const commentMessageTypes = [
    NotificationSourceType.BoardCommentAdded,
    NotificationSourceType.CardCommentAdded,
    NotificationSourceType.MentionedInBoardComment,
    NotificationSourceType.MentionedInCardComment,
];

const markdownTypes = [
    NotificationSourceType.MentionedInRoadmapCardComment,
    NotificationSourceType.BoardCommentAdded,
    NotificationSourceType.CardCommentAdded,
    NotificationSourceType.MentionedInStorymapCardDescription,
    NotificationSourceType.MentionedInCardComment,
    NotificationSourceType.MentionedInBoardComment,
    NotificationSourceType.MentionedInIdeaComment,
    NotificationSourceType.MentionedInRoadmapIdeaComment,
    NotificationSourceType.MentionedInFeedbackComment,
];

const acceptableInvitationTypes = [NotificationSourceType.InvitedToStorymap, NotificationSourceType.InvitedToWorkspace];

export class UserNotificationModel {
    id: string = '';
    notificationSourceType: NotificationSourceType;
    notificationSourceId: string = '';
    notificationText: string = '';
    relatedCardId: string = '';
    relatedCardName: string = '';
    sourceUserName: string = '';
    sourceUserId: string = '';
    happenedAt: string = '';
    isDismissed: boolean = false;
    isUpdated: boolean = false;
    storymapId: string = '';
    storymapSlug: string = '';
    storymapName: string = '';
    roadmapId: string = '';
    roadmapName: string = '';
    workspaceId: number;
    workspaceSubdomain: string = '';
    workspaceName: string = '';
    productSlug: string = '';
    replies: UserNotificationReply[];

    isDirectMessage = computed(() => directMessageTypes.includes(this.notificationSourceType));

    isComment = computed(() => commentMessageTypes.includes(this.notificationSourceType));

    isAcceptableInvitation = computed(() => {
        return (
            acceptableInvitationTypes.includes(this.notificationSourceType) &&
            !!this.notificationSourceId &&
            this.notificationSourceId !== '00000000-0000-0000-0000-000000000000' &&
            this.notificationSourceId !== 'AAAAAAAAAAAAAAAAAAAAAA'
        );
    });

    isMarkdownContent = computed(() => markdownTypes.includes(this.notificationSourceType));

    notificationMarkdownContent = computed(() => {
        if (!this.isMarkdownContent.value) {
            return '';
        }

        const { mdViewer } = useNotificationMdParser();

        return mdViewer.toHtml(this.notificationText);
    });
}

export class UserNotificationReply {
    CommentText: string;
    CommentedAt: Date;

    constructor(CommentText: string, CommentedAt: Date) {
        this.CommentText = CommentText;
        this.CommentedAt = CommentedAt;
    }

    commentMarkdownContent = computed(() => {
        const { mdViewer } = useNotificationMdParser();

        return mdViewer.toHtml(this.CommentText);
    });
}
